import {
  Condition,
  DetailedInsight,
  Entity,
  Episode,
  HighConvictionReport,
  InsightsPrediction,
  Period,
  Prediction,
} from '@toggle/toggle';
import { ReactNode } from 'react';

import type {
  ChartData,
  DocumentData,
  ScenarioData,
  ScreeningData,
  WatchlistData,
} from './assistant-response.type';

export enum Feedback {
  Neutral = 'Neutral',
  Bad = 'Bad',
  Good = 'Good',
}

export type MessageStatus = 'pending' | 'complete' | 'error' | 'abort';
export interface Message {
  id: string;
  question: string;
  type?: 'reset' | 'alfred';
  status: MessageStatus;
  reply: MessageReply[];
  hasSeen: boolean;
}

export interface ChatStore {
  sessionId: string;
  messages: Message[];
  controller: AbortController;
  abortRequest: () => void;
  isMessagePending: boolean;
  resetSession: () => void;
  addFeedback: ({
    sessionId,
    requestId,
    feedback,
  }: {
    sessionId: string;
    requestId: string;
    feedback: Feedback;
  }) => void;
  sendMessage(props: {
    question: string;
    target?: Source;
    abortSignal?: AbortSignal;
  }): Promise<void>;
  resetStore: () => void;
  append(message: Message): void;
  setActionsFromReply(reply: MessageReply): void;
  removeReply(messageId: string, source: Source): void;
  update(message: Partial<Message>): void;
}

export type Source = MessageReply['source'];

export type DocumentResponse = {
  source: 'document-assistant';
  data: DocumentData;
  request_id: string;
  feedback?: Feedback;
};

export type WatchlistResponse = {
  source: 'watchlist-assistant';
  data: WatchlistData;
  request_id: string;
  feedback?: Feedback;
};

type ScenarioResponse = {
  source: 'scenario-assistant';
  data: ScenarioData;
  request_id: string;
  feedback?: Feedback;
};

export type ScreeningResponse = {
  source: 'screening-assistant';
  data: ScreeningData;
  request_id: string;
  feedback?: Feedback;
};

export type ChartResponse = {
  source: 'chart-assistant';
  data: ChartData;
  request_id: string;
  feedback?: Feedback;
};

export type FallBackResponse = {
  source: 'fallback-assistant';
  data: { answer: string; confidence: number };
  request_id: string;
  feedback?: Feedback;
};

type ChatInsightResponse = {
  source: 'insight-widget';
  data: InsightWidgetData;
};

type ChatScenarioResponse = {
  source: 'scenario-widget';
  data: ScenarioWidgetData;
};

export type AssetOverviewAssistantResponse = {
  source: 'asset-overview-assistant';
  data: { tag: string; confidence: number };
};

type AssetOverviewResponse = {
  source: 'asset-overview-widget';
  data: AssetOverviewData;
};

type PastEpisodesResponse = {
  source: 'past-episodes-widget';
  data: PastEpisodesData;
};

type QualityScoreResponse = {
  source: 'chat-quality-score';
  data: ChatQualityScoreData;
};

export type MessageReply =
  | DocumentResponse
  | WatchlistResponse
  | ScenarioResponse
  | ChartResponse
  | ScreeningResponse
  | ChatInsightResponse
  | ChatScenarioResponse
  | PastEpisodesResponse
  | QualityScoreResponse
  | AssetOverviewAssistantResponse
  | AssetOverviewResponse
  | FallBackResponse;

export interface ChatQualityScoreData {
  isFixedIncome?: boolean;
  gaugeValue: number;
  horizon: Period;
  median?: number;
  checklist: HighConvictionReport;
  titleElement?: ReactNode;
  confidence?: number;
  hasHeader?: boolean;
}

export type InsightWidgetData = {
  insight: DetailedInsight;
  predictions: InsightsPrediction[];
  entity: Entity;
  confidence?: number;
};

export interface ScenarioWidgetData {
  entityTag: string;
  conditions: Condition[];
  prediction: Prediction[];
  episodes: Episode[];
  period?: Period;
  confidence: number;
}

export interface AssetOverviewData {
  entity: Entity;
  confidence: number;
}

export type PastEpisodesData =
  | ScenarioPastEpisodesData
  | InsightPastEpisodesData;

export interface ScenarioPastEpisodesData {
  variant: 'scenario-past-episodes';
  episode: Episode;
  confidence?: number;
}

export interface InsightPastEpisodesData {
  variant: 'insight-past-episodes';
  insight: DetailedInsight;
  entity: Entity;
  confidence?: number;
  hasHeader?: boolean;
}
